/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable indent */

import { getDatepickerConfig } from '@/components/EpDatepicker/utils/getDatepickerConfig';
import { handleOnChange } from '@/components/EpDatepicker/utils/handleOnChange';
import { EpInput } from '@/components/EpInput';
import { useDatepickerMask } from '@/hooks/useDatepickerMask';
import { EpDatepickerProps, IDateOptions } from '@/interfaces';
import { getDatepickerLocaleProps } from '@/utils/getDatepickerLocaleProps';
import { takeLocales } from '@/utils/takeLocales';
import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import React, { Ref, useEffect, useRef } from 'react';
import './EpDatepicker.scss';

export const dateOptions = ({ count, type }: IDateOptions) => {
  return {
    years: type === 'year' ? count : 0,
    months: type === 'month' ? count : 0,
    weeks: type === 'week' ? count : 0,
    days: type === 'day' ? count : 0,
  };
};

export const EpDatepicker: React.FC<EpDatepickerProps> = ({
  datepickerId,
  cultureCode,
  customClasses,
  isInlineDatepicker = false,
  isManualEntry = true,
  datepickerInputName,
  datepickerSize = 'md',
  datepickerOnChangeFunc,
  datepickerMinView = 'days',
  datepickerView = 'days',
  datepickerDateFormat,
  reactHookFormValue,
  reactHookFormGetValues,
  inputHasIcon,
  inputIconName = 'RegularCalendarAlt',
  inputIsFullWidth,
}) => {
  const datepickerInstanceRef = useRef<AirDatepicker<HTMLElement> | null>(null);
  const { iMask, dateFormatByCultureCode } = useDatepickerMask({
    cultureCode,
    instanceRef: datepickerInstanceRef,
  });

  const maskedInputRef = iMask?.ref as Ref<HTMLInputElement> | undefined;

  useEffect(() => {
    const language = takeLocales(cultureCode);
    const locale = getDatepickerLocaleProps(language);
    const selectedDates: any[] = [];
    const configProps = {
      isInline: isInlineDatepicker,
      firstDay: 0,
      locale,
      cultureCode,
      multipleDates: false,
      selectedDates,
      view: datepickerView,
      minView: datepickerMinView,
      dateFormat: datepickerDateFormat,
      onSelect: ({ date }: any) => handleOnChange({ date: date, onChange: datepickerOnChangeFunc })
    };

    if (reactHookFormGetValues) {
      const selectedDate = reactHookFormGetValues(datepickerInputName);
      if (selectedDate && selectedDate.questionId && selectedDate.inputValue) {
        selectedDates.push(selectedDate.inputValue[0]);
      } else {
        selectedDates.push(selectedDate);
      }
    }

    if (datepickerInstanceRef.current) {
      datepickerInstanceRef.current.update(getDatepickerConfig({ ...configProps }));
    } else {
      datepickerInstanceRef.current = new AirDatepicker(`#${datepickerId}`, getDatepickerConfig({ ...configProps }));
    }
  }, [cultureCode, datepickerId, datepickerOnChangeFunc, isInlineDatepicker, reactHookFormGetValues, datepickerInputName, datepickerView, datepickerMinView, datepickerDateFormat]);

  return (
    <>
      {isInlineDatepicker ? (
        <div
          id={datepickerId}
          className={`ep-datepicker ${customClasses ? customClasses : ''}`}
        ></div>
      ) : (
        <EpInput
          inputId={datepickerId}
          inputSize={datepickerSize}
          inputName={datepickerInputName}
          {...(isManualEntry
            ? { ref: maskedInputRef, inputPlaceholder: dateFormatByCultureCode }
            : { inputValue: reactHookFormValue })}
          customClasses={`ep-datepicker ${customClasses ? customClasses : ''}`}
          inputHasIcon={inputHasIcon}
          inputIconName={inputIconName}
          inputIsFullWidth={inputIsFullWidth}
        />
      )}
    </>
  );
};
